import type { ChamberSceneModel } from './chamber3d/chamberScene.ts'
import type { ChamberToolDrawing } from './chamberOutline.ts'
import { TRAY_CELL_H, TRAY_CELL_W } from './TrayFigure.tsx'

/**
 * **챔버 한 칸 — 정면(옆에서 본 단면) 기하**(eric 2026-10-08 「그럼.. 정면도로 바꾸자.. 구현해줘」).
 *
 * 장비 판(원심 그리퍼 · 어셈블러 RGD 그리퍼 + 압착판 + 아래 UV · 염색기 ZEUS 피펫)이 전부 정면이라 시료 카드의 챔버도 같은 시점으로 선다.
 * 정면에서는 **층**이 보인다 — 바닥 젤 층(PEGDA) · 그 위 세포층(원심이 가라앉힌 것) · 젤 막(UV 가 굳힌 것) · 그 위 액체(부유액 · 시약).
 * ⚠ 2026-09-29 의 「위에서 본 평면도」(`chamberOutline.ts`)를 뒤집은 것이다 — 그 파일은 장비 관점 앱 등 다른 자리가 아직 읽어 남겨 둔다.
 *
 * 틀은 종전과 같은 **72 × 50**(`TRAY_CELL_W/H`) — 카드 배치가 안 흔들린다. 좌표는 그림 단위(정수 · 0.5 단위)다.
 *
 * 🔴 **겉모습은 실물 하이픽 챔버(CTCells · `docs/stl/hy_box.stl`)의 정면 실루엣이다**(eric 2026-10-12 「이거... 하이픽 챔버 느낌이 안나...」 —
 *    종전 「네모 몸통 + 납작한 받침판」은 노트북처럼 읽혔다). 실측(`docs/figures/hypicc/README.md` §0-2 · mm): 받침(플랜지) 41 × 2 ·
 *    어깨가 z 2…12 에서 41 → 24.8 로 좁아지고(곧은 빗변으로 그린다) · 목 24.8(z 12…19.5) · 구멍 22.4(위아래로 뚫림). **1 mm = 1.5 그림 단위**로 옮겼다.
 *
 * ```
 *  y  0 ┌────────────────────────────── 72 ─┐
 *       │            ▼ 도구(팁 · 압착 · 그리퍼)│
 *    17 │          ┌┬──────────────┬┐        │ ← 목 윗변(입구) · 벽 두께 1.8
 *       │          ││  ~~ 액체 ~~  ││   ≋    │ ← 원심: 속도선 + 아래 화살표
 *    28 │          ││              ││        │ ← 어깨가 시작한다(곡면)
 *    37 │        ╱ ││▓▓ 세포층 · 막 ││ ╲      │
 *    40 │      ╱   ││▒▒▒ 젤 층 ▒▒▒▒││   ╲    │
 *    43 │  ┌──┴────┴┴──────────────┴┴────┴──┐│ ← 받침(플랜지) 윗변 · 우물 바닥
 *    46 │  └─────────────────────────────────┘│
 *    50 └ ↑ ↑ ↑ UV(어셈블러) ─────────────────┘
 * ```
 */
const W = TRAY_CELL_W
const H = TRAY_CELL_H

/** 1 mm → 그림 단위 */
const MM = 1.5
const CX = W / 2
/** 가운데 기준 좌우 — 소수 둘째 자리로 자른다(경로에 긴 꼬리가 안 붙게) */
const half = (mm: number) => Math.round(((mm * MM) / 2) * 100) / 100

/** 몸통(목) 겉 · 우물(구멍) · 받침(플랜지) · 어깨 — 그림 단위 */
export const FRONT = {
  /** 목 — 바깥 24.8 mm · 위 z 19.5 → 어깨 아래까지 이어진 벽 */
  body: { x0: CX - half(24.8), x1: CX + half(24.8), y0: 17, y1: 43 },
  /** 구멍 — 22.4 mm · 바닥은 받침 윗변(실물은 뚫려 슬라이드가 바닥이다) */
  well: { x0: CX - half(22.4), x1: CX + half(22.4), y0: 17, y1: 43 },
  /** 받침(플랜지) — 41 × 2 mm */
  plate: { x0: CX - half(41), x1: CX + half(41), y0: 43, y1: 46 },
  /** 어깨 — z 12(y 28)에서 받침까지 곧은 빗변으로 퍼진다(eric 2026-10-12 「직선으로」) · 받침 끝보다 조금 안쪽에서 내려앉는다(받침 턱이 보이게) */
  shoulder: { y0: 28, inset: 1.5 },
  /** 층 윗변 — 젤 층 · 세포층 · 젤 막 · 액체(절반 · 가득) */
  bedTop: 40,
  sedimentTop: 38,
  filmTop: 37,
  halfTop: 30,
  fullTop: 20,
} as const

/* 꼭짓점을 다 적는다(M · L) — 시험이 경로의 점으로 크기를 잰다 */
const rect = (x0: number, y0: number, x1: number, y1: number) =>
  `M${String(x0)} ${String(y0)}L${String(x1)} ${String(y0)}L${String(x1)} ${String(y1)}L${String(x0)} ${String(y1)}Z`

/** 몸 — 상태와 무관하다(실물은 늘 같다) */
export function chamberFrontBody(): {
  plate: string
  feet: string[]
  shell: string
  wall: string
  well: string
  opening: string
} {
  const { body, well, plate, shoulder } = FRONT
  const foot0 = plate.x0 + shoulder.inset
  const foot1 = plate.x1 - shoulder.inset
  const n = (v: number) => String(v)
  return {
    plate: rect(plate.x0, plate.y0, plate.x1, plate.y1),
    /*
     * 어깨 둘 — 목 아래에서 받침으로 퍼지는 면(실물의 어깨 + 모서리 보강 리브 자리). 🔴 **곧은 빗변**이다 — 곡선(둥근 원호)은
     * eric 이 걷었다(2026-10-12 「보조 테두리를 직선으로 표시해주고.. 지금 둥근 원이잖아」). 옅은 단 · 선 없음 — 윤곽은 상태 선(`__shell`)이 진다
     */
    feet: [
      `M${n(body.x0)} ${n(shoulder.y0)}L${n(foot0)} ${n(body.y1)}L${n(body.x0)} ${n(body.y1)}Z`,
      `M${n(body.x1)} ${n(shoulder.y0)}L${n(foot1)} ${n(body.y1)}L${n(body.x1)} ${n(body.y1)}Z`,
    ],
    /* 겉 윤곽 — 받침 위에 앉은 어깨 · 목(받침은 따로) */
    shell:
      `M${n(foot0)} ${n(body.y1)}L${n(body.x0)} ${n(shoulder.y0)}` +
      `L${n(body.x0)} ${n(body.y0)}L${n(body.x1)} ${n(body.y0)}L${n(body.x1)} ${n(shoulder.y0)}` +
      `L${n(foot1)} ${n(body.y1)}Z`,
    /* 벽 = 목 − 우물(위가 트였다) */
    wall: `${rect(body.x0, body.y0, body.x1, body.y1)} ${rect(well.x0, well.y0, well.x1, well.y1)}`,
    well: rect(well.x0, well.y0, well.x1, well.y1),
    opening: `M${n(well.x0)} ${n(well.y0)}H${n(well.x1)}`,
  }
}

/** 우물 안 층 — 장면 모델(`chamberScene`)이 고른 것만 */
export function chamberFrontInside(
  model: Pick<ChamberSceneModel, 'bed' | 'fill' | 'liquid' | 'pellicle'> &
    Partial<Pick<ChamberSceneModel, 'sediment'>>,
): {
  bed: string | null
  liquid: { d: string; level: 'half' | 'full' } | null
  film: string | null
  sediment: string | null
} {
  const { well } = FRONT
  const bed = model.bed ? rect(well.x0, FRONT.bedTop, well.x1, well.y1) : null
  const sediment =
    model.sediment === true ? rect(well.x0, FRONT.sedimentTop, well.x1, FRONT.bedTop) : null
  const film = model.pellicle ? rect(well.x0, FRONT.filmTop, well.x1, FRONT.bedTop) : null
  const floor = model.bed ? FRONT.bedTop : well.y1
  const level = model.fill >= 1 ? ('full' as const) : ('half' as const)
  const liquid =
    model.fill > 0 && model.liquid !== 'none'
      ? {
          d: rect(well.x0, level === 'full' ? FRONT.fullTop : FRONT.halfTop, well.x1, floor),
          level,
        }
      : null
  return { bed, liquid, film, sediment }
}

/**
 * 두께 조절기(TC) — 우물에 위에서 꽂힌 **U 틀**(정면) + 위 손잡이 가로대. 장비 판 어셈블러의 TC 틀과 같은 꼴이다
 */
export function chamberFrontController(): { frame: string; handle: string } {
  const top = 8
  const bottom = FRONT.filmTop
  return {
    frame: `M24 ${String(top + 3)}V${String(bottom)}H48V${String(top + 3)}H45V${String(bottom - 3)}H27V${String(top + 3)}Z`,
    handle: rect(20, top, 52, top + 3),
  }
}

/** 도구 — 단계마다 하나(`chamberTool()`). 장비 판과 같은 말을 정면으로 */
export function chamberFrontTool(tool: ChamberSceneModel['tool']): ChamberToolDrawing {
  const empty: ChamberToolDrawing = { shapes: [], ripples: [], lines: [], bulbs: [], dots: [] }
  switch (tool) {
    case 'spin':
      /*
       * 원심 — 우물 안 굵은 아래 화살표 셋(원심력이 세포를 젤 층 쪽으로 누른다). 속도선은 몸과 따로 선다(`chamberFrontSpeed` — 몸이 궤도를
       * 도는 동안 제자리에서 흐른다). eric 2026-10-12 「원심분리기 회전할 때.. 시료 화면의 챔버 확실하게 변환해줘」 — 종전 가는 화살 둘 · 짧은 선 셋은
       * 카드 크기에서 안 보였다
       */
      return {
        ...empty,
        lines: [
          'M27 21v11M23.5 28.5l3.5 3.5 3.5-3.5',
          'M36 21v11M32.5 28.5l3.5 3.5 3.5-3.5',
          'M45 21v11M41.5 28.5l3.5 3.5 3.5-3.5',
        ],
      }
    case 'press':
      /*
       * 어셈블러 — 조절기(TC 틀)가 꽂혀 눌린 모습 그 자체가 말한다. 🔴 손잡이 위 ↓ 화살표는 걷었다 — 작은 칸에서 가운데 위의 가는 막대로 보여
       * 염색기 피펫 팁(스포이드)과 헷갈렸다(eric 2026-10-08 「어셈블러에 스포이드 같은 게 중간에 보이는데 그게 뭐 하는 거야?」)
       */
      return empty
    case 'uv':
      /* 어셈블러 UV — 받침 아래에서 위로 드는 빛줄기(장비 판 램프와 같은 방향) */
      return { ...empty, lines: ['M26 50v-3', 'M32 50v-3', 'M40 50v-3', 'M46 50v-3'] }
    case 'pipette':
      /*
       * 염색기 — 피펫(ZEUS LT 정면 · 몸통 · 목 · 팁)이 위에서 우물 «안»으로 들어가 팁 끝이 액체에 닿는다 + 방울 하나.
       * eric 2026-10-12 「분주할 때.. 피펫 넣는 거 표현해줘」 — 종전 가는 원뿔 하나(우물 입구까지)는 카드 크기에서 안 보였다
       */
      return {
        ...empty,
        shapes: ['M31 0H41V8H31Z', 'M34 8H38V11H34Z', 'M34 11H38L36.5 28H35.5Z'],
        dots: [{ x: 36, y: 30.5, r: 1.4 }],
      }
    default:
      return empty
  }
}

/**
 * 원심 회전의 속도선 — 몸통 양옆 위쪽(어깨가 퍼지기 전 높이)에 셋씩. 🔴 몸(`__rig`)과 따로 그린다 — 몸이 로터 궤도를 따라 도는 동안
 * 선은 제자리에서 흐른다(`figures.css` fig-cs-speed). 왼쪽은 길이를 엇갈려 「지나간 자국」으로 읽힌다
 */
export function chamberFrontSpeed(): string[] {
  return ['M1 19h11', 'M3 25.5h9', 'M1 32h11', 'M60 19h11', 'M60 25.5h9', 'M60 32h11']
}

/** 흔들기 — 몸통 양옆의 떨림 표지(셰이커가 판째 흔든다) */
export function chamberFrontShake(): string[] {
  return ['M9 22l-3 4 3 4', 'M63 22l3 4-3 4']
}

/** 중단 · 폐기 — 우물을 가로지르는 막대 */
export function chamberFrontBar(): string {
  return `M${String(FRONT.body.x0 - 4)} 27H${String(FRONT.body.x1 + 4)}`
}

/** 틀 크기(시험용) */
export const FRONT_BOX = { w: W, h: H } as const
